/**
 * 暖棕摩卡模板 - 登录页
 *
 * 设计理念：咖啡馆质感 + 商务杂志目录
 * - 右侧(42%)：深棕渐变品牌区（镜像 red 的左品牌区）
 * - 左侧(58%)：暖米白表单区 + 方角商务卡片
 */

import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function MochaLoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { siteConfig } = useSiteConfig();

  const from = (location.state as { from?: string })?.from || "/";

  const siteLogo = siteConfig?.logo || "https://public.readdy.ai/ai/img_res/1ebe5b90-bc3d-41ad-9f51-693742fb28f5.png";
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "商服汇";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!username.trim()) { setError("请输入用户名"); return; }
    if (!password.trim()) { setError("请输入密码"); return; }

    const result = await login(username, password);
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.message || "登录失败");
    }
  };

  return (
    <div className="min-h-screen flex bg-[#FAF6F0]">
      {/* ── 左侧表单区（58%）── */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 sm:px-12 lg:px-16 xl:px-24">
        <div className="w-full max-w-[440px]">
          {/* 移动端 Logo */}
          <div className="lg:hidden mb-10">
            <Link to="/" className="flex items-center gap-2.5">
              {siteLogo && (
                <img src={siteLogo} alt={siteDisplayName} className="h-10 w-10 object-contain" />
              )}
              <span className="text-xl font-bold text-[#4A3525]">{siteDisplayName}</span>
            </Link>
          </div>

          {/* 返回链接 */}
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#B7A892] hover:text-[#C77B3F] transition-colors mb-8 group"
          >
            <i className="ri-arrow-left-line group-hover:-translate-x-0.5 transition-transform" />
            返回首页
          </Link>

          {/* 标题 */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-0.5 bg-[#C77B3F]" />
              <span className="text-xs font-bold tracking-[0.2em] text-[#C77B3F]">WELCOME BACK</span>
            </div>
            <h1 className="text-3xl font-bold text-[#4A3525]">登录账户</h1>
            <p className="text-[#8A7B6D] mt-2">请输入您的账户信息以继续</p>
          </div>

          {/* 登录表单：方角商务卡片 */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 用户名 */}
            <div>
              <label className="block text-sm font-medium text-[#4A3525] mb-2">用户名</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i className="ri-user-3-line text-[#B7A892] text-lg" />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="请输入用户名"
                  className="w-full pl-11 pr-4 py-3.5 bg-white border border-[#E8DCC8] rounded-lg text-sm text-[#2B241D] placeholder-[#B7A892] focus:outline-none focus:ring-2 focus:ring-[#C77B3F]/50 focus:border-[#C77B3F] focus:bg-white transition-all"
                />
              </div>
            </div>

            {/* 密码 */}
            <div>
              <label className="block text-sm font-medium text-[#4A3525] mb-2">密码</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i className="ri-lock-2-line text-[#B7A892] text-lg" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  className="w-full pl-11 pr-12 py-3.5 bg-white border border-[#E8DCC8] rounded-lg text-sm text-[#2B241D] placeholder-[#B7A892] focus:outline-none focus:ring-2 focus:ring-[#C77B3F]/50 focus:border-[#C77B3F] focus:bg-white transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-[#B7A892] hover:text-[#4A3525] cursor-pointer rounded-lg hover:bg-[#FAF6F0] transition-colors"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                </button>
              </div>
            </div>

            {/* 记住我 & 忘记密码 */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-[#E8DCC8] accent-[#C77B3F] cursor-pointer"
                />
                <span className="text-sm text-[#8A7B6D] group-hover:text-[#4A3525] transition-colors">记住我</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-[#C77B3F] hover:text-[#B0672F] font-medium transition-colors">
                忘记密码？
              </Link>
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="p-4 bg-[#C77B3F]/10 text-[#B0672F] text-sm rounded-lg flex items-center gap-3 border border-[#C77B3F]/30">
                <i className="ri-error-warning-line shrink-0 text-lg" />
                <span>{error}</span>
              </div>
            )}

            {/* 登录按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-[#4A3525] text-[#F5EBDD] font-semibold rounded-lg hover:bg-[#3A2A1D] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#4A3525]/20 hover:shadow-[#4A3525]/35 text-base"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  登录中...
                </span>
              ) : (
                "登  录"
              )}
            </button>
          </form>

          {/* 底部注册链接 */}
          <div className="mt-8 text-center">
            <p className="text-sm text-[#8A7B6D]">
              还没有账户？{" "}
              <Link to="/register" className="text-[#C77B3F] font-semibold hover:text-[#B0672F] transition-colors underline underline-offset-2">
                立即注册
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* ── 右侧品牌区（42%）镜像 red ── */}
      <div className="hidden lg:flex lg:w-[42%] xl:w-[40%] relative overflow-hidden bg-gradient-to-br from-[#4A3525] via-[#3F2C1F] to-[#2B1D14]">
        {/* 焦糖装饰圆环 */}
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full border border-[#C77B3F]/20" />
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full border border-[#C77B3F]/15" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#C77B3F]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-[#DCA96B]/10 rounded-full blur-3xl" />

        {/* 细线纹理 */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />

        {/* 内容 */}
        <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">
          {/* 顶部 Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            {siteLogo && (
              <div className="w-12 h-12 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center p-1.5 overflow-hidden shadow-lg shadow-black/20 border border-[#C77B3F]/30">
                <img src={siteLogo} alt={siteDisplayName} className="w-full h-full object-contain brightness-0 invert" />
              </div>
            )}
            <span className="text-2xl font-bold text-[#F5EBDD] tracking-tight">{siteDisplayName}</span>
          </Link>

          {/* 中间核心文案 */}
          <div className="flex-1 flex flex-col justify-center">
            <h2 className="text-4xl xl:text-5xl font-bold text-[#F5EBDD] leading-tight mb-6">
              欢迎回来
              <br />
              <span className="text-[#DCA96B]">继续您的商业之旅</span>
            </h2>
            <p className="text-[#C9B8A2] text-lg leading-relaxed max-w-sm">
              登录您的账户，管理商业服务、查看最新动态，让业务更高效。
            </p>

            {/* 特色亮点 */}
            <div className="mt-12 space-y-5">
              {[
                { icon: "ri-shield-check-line", text: "企业级安全防护，数据加密传输" },
                { icon: "ri-flashlight-line", text: "一键登录，快速进入工作台" },
                { icon: "ri-customer-service-2-line", text: "7×24 小时专属客服支持" },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 text-[#EADFCE]/90 hover:text-white transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-[#C77B3F]/15 border border-[#C77B3F]/30 flex items-center justify-center shrink-0 text-[#DCA96B]">
                    <i className={`${item.icon} text-lg`} />
                  </div>
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 底部版权 */}
          <p className="text-[#8A7B6D] text-xs">
            &copy; {new Date().getFullYear()} {siteDisplayName}. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
